import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    Button,
    Search,
    Modal,
    Group,
    Select,
    ContextualMenu,
    MenuList,
    MenuItem,
    useToast,
    Pagination
} from '@veneer/core';
import { BsPencil } from "react-icons/bs";
import { GrClose } from "react-icons/gr";
import fetchUsersData from "../../../../api/users";
import { useLocation } from "react-router";

const ROLE_MAP = {
    1: 'Admin',
    2: 'Service Authorized Partner',
    3: 'Call Center',
    4: 'Authorized Return Center'
};

const reverseRoleMap = Object.fromEntries(
    Object.entries(ROLE_MAP).map(([key, value]) => [value, Number(key)])
);

const Users = () => {
    const [searchText, setSearchText] = useState('');
    const [usersData, setUsersData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [regions, setRegions] = useState([]);
    const [role, setRole] = useState();
    const [status, setStatus] = useState();
    const [isSaveEnabled, setIsSaveEnabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const { addToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [tokenInfo, setTokenInfo] = useState(null);
    const location = useLocation();
    const [hasRedirected, setHasRedirected] = useState(false);

    const base64UrlDecode = (base64Url) => {
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const padding = '='.repeat((4 - base64.length % 4) % 4);
        return decodeURIComponent(escape(atob(base64 + padding)));
    };

    const parseJwt = (token) => {
        const [header, payload] = token.split('.');
        return {
            header: JSON.parse(base64UrlDecode(header)),
            payload: JSON.parse(base64UrlDecode(payload))
        };
    };

    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
    };

    function removeCookie(name, path = '/') {

        const cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path};`;

        // Remove the cookie
        document.cookie = cookieString;
    }
    const setRedirectURL = useCallback(() => {
        if (!localStorage.getItem('redirectURL')) {
            localStorage.setItem('redirectURL', window.location.href);
        }
    }, []);

    const handleLogout = useCallback(() => {
        removeCookie('auth-token');
        setRedirectURL();
        const logoutUrl = `${process.env.REACT_APP_PHPSC_LOGOUT_AUTH_URI}/?post_logout_redirect_uri=${process.env.REACT_APP_PHPSC_LOGOUT_REDIRECT_URI}/&state=sign_out`
                    .replace(/([^:]\/)\/+/g, "$1");
                    window.location.href = logoutUrl;
    }, [setRedirectURL]);

    const filterData = useCallback((data, value) => {
        return data.filter((item) =>
            Object.values(item).some((field) =>
                typeof field === 'string' && field.toLowerCase().includes(value.toLowerCase())
            )
        );
    }, []);

    const fetchAndSetUserData = async () => {
        try {
            const data = await fetchUsersData();
            if (data && Array.isArray(data.users)) {
                const formattedData = data.users.map(user => ({
                    ...user,
                    profile: ROLE_MAP[user.profile] || 'Unknown'
                }));
                setUsersData(formattedData);
                setFilteredData(formattedData);
            } else {
                console.error('API returned data that is not an array:', data);
                setUsersData([]);
                setFilteredData([]);
            }
        } catch (error) {
            console.error('Error fetching user data:', error.message);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const storedToken = getCookie('auth-token');
                if (storedToken) {
                    const { header, payload } = parseJwt(storedToken);
                    const storedUserRoleId = header.profile || payload.profile;
                    if (storedUserRoleId != 1) {
                        window.location.href = '/';
                        return;
                    }
                    const tokenExpiration = (header.exp || payload.exp) * 1000; // Convert expires_in from seconds to milliseconds
                    const currentTime = Date.now();

                    if (currentTime < tokenExpiration) {
                        setTokenInfo(header || payload);
                        const redirectURL = localStorage.getItem('redirectURL');
                        if (redirectURL && !hasRedirected) {
                            localStorage.removeItem('redirectURL');
                            setHasRedirected(true);
                            window.location.href = redirectURL;
                            return;
                        }
                        await fetchAndSetUserData(); // Fetch and set user data
                    } else {
                        handleLogout();
                        return;
                    }
                } else {
                    setRedirectURL();
                    window.location.href = '/';
                    return;
                }
            } catch (error) {
                console.error('Error fetching token:', error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [location, filterData, hasRedirected, setRedirectURL]);

    useEffect(() => {
        const isEmailValid = email.trim() !== '';
        const isNameFilled = name.trim() !== '';
        const areRegionsSelected = regions.length > 0;

        setIsSaveEnabled(isNameFilled && isEmailValid && areRegionsSelected);
    }, [email, name, regions]);

    const handleSearchChange = (value) => {
        setSearchText(value);
        const filtered = filterData(usersData, value);
        setFilteredData(filtered);
        setCurrentPage(1);
    };

    const columns = [
        { id: 'user_email', label: 'Email' },
        { id: 'description', label: 'Description' },
        { id: 'region', label: 'Region' },
        { id: 'profile', label: 'Role' },
        { id: 'status', label: 'Status' },
        { id: 'reviewButton', label: '' }
    ];

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const pagedData = filteredData.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const generateReviewButton = (rowData) => {
        return (
            <ContextualMenu
                anchorNode={<Button small>Edit</Button>}
                placement="top"
            >
                <MenuList>
                    <MenuItem
                        label="Edit user"
                        leadingArea={<BsPencil />}
                        onClick={() => {
                            setEditingUser(rowData.id);
                            setName(rowData.description);
                            setEmail(rowData.user_email);
                            setRegions(rowData.region.split(/\s*,\s*/));
                            setRole([reverseRoleMap[rowData.profile] || '']);
                            setStatus([rowData.status]);
                            setIsEditModalOpen(true);
                        }}
                        style={{ backgroundColor: "white" }}
                    />
                    <MenuItem
                        label="Delete user"
                        leadingArea={<GrClose />}
                        onClick={() => handleDeleteUser(rowData.id)}
                        style={{ backgroundColor: "white" }}
                    />
                </MenuList>
            </ContextualMenu>
        );
    };

    const handleSaveUser = () => {
        const newUser = {
            user_email: email,
            description: name,
            profile: reverseRoleMap[role] || 'Unknown',
            region: regions.join(', '),
            status: 'New'
        };

        setUsersData([...usersData, newUser]);
        setFilteredData([...filteredData, newUser]);

        setIsModalOpen(false);
        setName('');
        setEmail('');
        setRole([]);
        setStatus([]);
        setRegions([]);
        setErrorMessage('');
        addToast({
            text: 'Successfully created the user',
            type: 'positive',
            timeout: 3,
            id: Date.now(),
        });
    };

    const handleDeleteUser = async (deletedUser) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_PHPSC_BACK_URL}/phpsc-backend/api/v1/users/${deletedUser}`, {
                method: 'DELETE',
                headers: {
                    'accept': 'application/json',
                }
            });

            if (response.ok) {
                addToast({
                    text: 'Successfully deleted the user',
                    type: 'positive',
                    timeout: 3,
                    id: Date.now(),
                });
                await fetchAndSetUserData(); // Refresh data after deletion
            } else {
                addToast({
                    text: "Failed to delete user",
                    type: 'negative',
                    timeout: 3,
                    id: Date.now(),
                });
            }
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    const handleEditUser = async () => {
        setIsSaveEnabled(false);
        try {
            const payload = {
                description: name,
                profile: role[0],
                region: regions.join(', '),
                status: status[0],
            };
            const response = await fetch(`${process.env.REACT_APP_PHPSC_BACK_URL}/phpsc-backend/api/v1/users/${editingUser}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                addToast({
                    text: 'Successfully updated the user',
                    type: 'positive',
                    timeout: 3,
                    id: Date.now(),
                });
                await fetchAndSetUserData(); // Refresh data after edit
            } else {
                addToast({
                    text: 'Failed to update user',
                    type: 'negative',
                    timeout: 3,
                    id: Date.now(),
                });
                 await fetchAndSetUserData();
            }
        } catch (error) {
            console.error('Error updating user:', error);
        } finally {
            setIsEditModalOpen(false);
        }
    };

    const rowsWithCombinedData = pagedData.map((item) => ({
        ...item,
        reviewButton: generateReviewButton(item)
    }));

    const column_width = [
        { columnId: 'user_email', width: 150 },
        { columnId: 'description', width: 150 },
        { columnId: 'region', width: 100 },
        { columnId: 'profile', width: 100 },
        { columnId: 'status', width: 100 },
        { columnId: 'reviewButton', width: 100 }
    ];

    const handleRegionChange = (event) => {
        const selectedValue = event.value;
        setRegions([selectedValue])
    };
    const handleStatusChange = (event) => {
        const selectedValue = event.value;
        setStatus([selectedValue])
    };
    const handleRoleChange = (event) => {
        const selectedValue = event.value;
        setRole([selectedValue]);
    };

    const onClear = () => setRegions([]);
    const onClearRole = () => setRole([]);
    const onClearStatus = () => setStatus([]);

    const regionOptions = [
        { value: 'EMEA', label: 'EMEA' },
        { value: 'NA', label: 'NA' },
        { value: 'APAC', label: 'APAC' },
        { value: 'LAT', label: 'LAT' },
    ];
    const statusOptions = [
        { value: 'new', label: 'New' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'rejected', label: 'Rejected' },
    ];

    const roleOptions = Object.entries(ROLE_MAP).map(([key, value]) => ({
        value: Number(key),
        label: value
    }));

    return (
        <>
            <div id='hp-admin-ui_page'>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 10 }}>
                    <h6 className='hp-serviceflex-ui_activity_table_title' style={{ marginRight: 'auto' }}>
                        <Search
                            label="Search"
                            placeholder="Search..."
                            value={searchText}
                            onChange={handleSearchChange}
                            data-testid="search-input"
                        />
                    </h6>
                    {/*  <Button
                        primary
                        onClick={() => setIsModalOpen(true)}
                    >
                        Create User
                    </Button>*/}
                </div>
                <Table
                    data={rowsWithCombinedData}
                    columns={columns}
                    preferences={{ width: column_width }}
                    loading={loading}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px' }}>
                    <Pagination
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        pageSize={pageSize}
                        totalItems={filteredData.length}
                    />
                </div>

                <Modal
                    align="start"
                    closeButton
                    id="create-user-modal"
                    className="custom-modal-delete"
                    show={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    footer={(
                        <Group>
                            <Button onClick={handleSaveUser} disabled={!isSaveEnabled}>Save</Button>
                            <Button appearance="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                        </Group>
                    )}
                    title="Create New User"
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label htmlFor="name" style={{ width: '200px', margin: 0 }}>Name:</label>
                            <input
                                id="name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', flex: 1 }}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label htmlFor="email" style={{ width: '200px', margin: 0 }}>Email:</label>
                            <input
                                id="email"
                                type="text"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', flex: 1 }}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label htmlFor="role" style={{ width: '310px', margin: 0 }}>Role:</label>
                            <Select
                                id="role"
                                options={roleOptions}
                                value={Array.isArray(role) ? role : []}
                                onChange={handleRoleChange}
                                label="Select Role(s)"
                                onClear={onClearRole}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label htmlFor="regions" style={{ width: '310px', margin: 0 }}>Regions:</label>
                            <Select
                                id="regions"
                                multiple
                                options={regionOptions}
                                value={Array.isArray(regions) ? regions : []}
                                onChange={handleRegionChange}
                                label="Select Region(s)"
                                onClear={onClear}
                            />
                        </div>

                        {errorMessage && <div style={{ color: 'red', textAlign: 'center' }}>{errorMessage}</div>}
                    </div>
                </Modal>

                <Modal
                    align="start"
                    closeButton
                    id="edit-user-modal"
                    className="custom-modal-delete"
                    show={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    footer={(
                        <Group>
                            <Button onClick={handleEditUser} disabled={!isSaveEnabled}>Save</Button>
                            <Button appearance="secondary" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
                        </Group>
                    )}
                    title="Edit User"
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label htmlFor="edit-name" style={{ width: '200px', margin: 0 }}>Description:</label>
                            <input
                                id="edit-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', flex: 1 }}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label htmlFor="edit-email" style={{ width: '200px', margin: 0 }}>Email:</label>
                            <input
                                id="edit-email"
                                type="text"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', flex: 1 }}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label htmlFor="edit-role" style={{ width: '310px', margin: 0 }}>Status:</label>
                            <Select
                                id="edit-role"
                                options={statusOptions}
                                value={Array.isArray(status) ? status : []}
                                onChange={handleStatusChange}
                                label="Select Status"
                                onClear={onClearStatus}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label htmlFor="edit-role" style={{ width: '310px', margin: 0 }}>Role:</label>
                            <Select
                                id="edit-role"
                                options={roleOptions}
                                value={Array.isArray(role) ? role : []}
                                onChange={handleRoleChange}
                                label="Select Role(s)"
                                onClear={onClearRole}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label htmlFor="edit-regions" style={{ width: '310px', margin: 0 }}>Regions:</label>
                            <Select
                                id="edit-regions"
                                multiple
                                options={regionOptions}
                                value={Array.isArray(regions) ? regions : []}
                                onChange={handleRegionChange}
                                label="Select Region(s)"
                                onClear={onClear}
                            />
                        </div>

                        {errorMessage && <div style={{ color: 'red', textAlign: 'center' }}>{errorMessage}</div>}
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default Users;
